import { Box, CircularProgress, IconButton, Typography, styled } from '@mui/material';
import Image from './Image';
import { Ref, forwardRef, useEffect, useRef, useState } from 'react';
import SvgIconStyle from './SvgIconStyle';
import { useDispatch, useSelector } from 'redux/store';
import { setShowEmpy, updateChatConversation } from 'redux/slices/global';
import { FormProvider, RHFTextField } from 'components/hook-form';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import * as services from 'services/empy';

// ----------------------------------------------------------------------

const WidgetStyle = styled('div')(() => ({
  boxShadow: '5px 10px 10px -5px rgb(0 0 0 / 40%)',
  position: 'absolute',
  margin: '16px 32px',
  padding: '16px',
  paddingRight: '64px',
  zIndex: 10000,
  bottom: 0,
  right: 0,
  width: 720,
  background: '#636B78',
  borderRadius: '32px',
  '@media (max-height: 600px)': {
    margin: '8px',
  },
}));

const FormContainerStyle = styled('div')(() => ({
  width: '100%',
  height: '100%',
  background: '#FFFFFF',
  borderRadius: '20px',
  position: 'relative',
  padding: '20px',
}));

const ArrowStyle = styled('div')(() => ({
  position: 'absolute',
  bottom: 20,
  right: -6,
  width: 0,
  height: 0,
  borderTop: '5px solid transparent',
  borderBottom: '5px solid transparent',
  borderLeft: '7px solid #FFFFFF',
}));

const BotStyle = styled(IconButton)(() => ({
  boxShadow: '5px 10px 10px -5px rgb(0 0 0 / 40%)',
  position: 'absolute',
  margin: '16px 32px',
  padding: 0,
  zIndex: 100,
  bottom: 0,
  right: 0,
  img: {
    width: 120,
    height: 64,
  },
  '@media (max-height: 600px)': {
    margin: '8px',
    img: { width: 90, height: 48 },
  },
}));

const TitleStyle = styled(Typography)(() => ({
  color: '#464E55',
  fontFamily: 'Quincy CF',
  fontSize: '33px',
  fontStyle: 'normal',
  fontWeight: 700,
  letterSpacing: '-0.5px',
  marginBottom: '28px',
  span: {
    color: '#CC6699',
  },
}));

const LabelStyle = styled(Typography)(() => ({
  color: '#CC6699',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 700,
  letterSpacing: '-0.5px',
  lineHeight: '16px',
}));

const TextFieldStyle = styled(RHFTextField)(({ theme }) => ({
  '.MuiOutlinedInput-root': {
    fontSize: '18px',
    padding: '10px 0 6px 0 !important',
    opacity: 1,
    fieldset: {
      borderColor: '#464E55',
      border: 0,
      borderBottom: 'solid 1px',
      borderRadius: 0,
      padding: 0,
    },
  },
  input: {
    fontSize: '18px',
    padding: '0 !important',
    textAlignLast: 'left',
  },
  '& input::placeholder': {
    fontSize: '18px',
  },
  margin: '0',
}));

const ButtonStyle = styled(LoadingButton)((props) => ({
  height: '38px',
  borderRadius: '22px',
  opacity: props.variant === 'outlined' ? 1 : 0.8,
  background:
    props.variant === 'outlined'
      ? '#FFFFFF'
      : 'linear-gradient(98.41deg, rgba(206, 104, 152, 0.8) 0%, rgba(252, 150, 106, 0.8) 100%)',
  color: props.variant === 'outlined' ? '#A3A9AD' : '#FFFFFF',
  textAlign: 'center',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  padding: '10px 20px',
  letterSpacing: '0.5px',
  boxShadow: 'none',
  border: props.variant === 'outlined' ? '#A3A9AD solid 1px' : 'none',
  '&:hover': {
    border: props.variant === 'outlined' ? '#A3A9AD solid 1px' : 'none',
    background:
      props.variant === 'outlined'
        ? '#FFFFFF'
        : 'linear-gradient(98.41deg, rgba(252, 150, 106, 0.8) 0%, rgba(206, 104, 152, 0.8) 100%)',
  },
  '&.Mui-disabled': {
    color: '#FFFFFF',
    opacity: 0.5,
  },
}));

const ContentStyle = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}));

const TopicStyle = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  image: {
    borderRadius: '10px',
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
  },
}));

const ChatStyle = styled(Box)(() => ({
  overflowY: 'auto',
  minHeight: '100px',
  maxHeight: '50vh',
  borderRadius: '8px',
  padding: '12px',
  '::-webkit-scrollbar': {
    width: '14px',
  },
  '::-webkit-scrollbar-track': {
    border: 'solid 2px #1A27311A',
    borderRadius: '18px',
  },
  '::-webkit-scrollbar-thumb': {
    background: '#CC6699',
    borderRadius: '18px',
  },
}));

const MessageStyle = styled('div')(() => ({
  position: 'relative',
  display: 'flex',
  gap: '4px',
  fontFamily: 'Inter',
  color: '#1A2731',
  fontSize: '16px',
  lineHeight: '24px',
  borderRadius: '20px',
  letterSpacing: '-0.5px',
  padding: '10px 20px',
  p: {
    opacity: 0.6,
    fontWeight: 300,
  },
  span: {
    opacity: 0.6,
    fontWeight: 600,
  },
  svg: {
    position: 'absolute',
    bottom: 0,
  },
}));

// ----------------------------------------------------------------------

const Logo = () => (
  <Box
    sx={{
      position: 'absolute',
      right: 14,
      bottom: 11,
    }}
  >
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24.7129 22.3478C24.1165 22.3478 22.5988 22.2528 21.3783 21.3832C20.905 21.0454 20.7437 20.4016 21.009 19.9183C21.2085 19.5573 21.5863 19.3314 21.9981 19.3314C22.2401 19.3314 22.4715 19.4074 22.6646 19.551C23.4075 20.0977 24.5771 20.1125 24.7087 20.1125C24.7554 20.1125 24.8891 20.1082 24.8891 20.1082C27.1157 20.1082 28.9241 18.3098 28.9241 16.0998C28.9241 14.3436 27.1964 12.4037 24.4073 12.334C24.2948 12.3319 24.1844 12.3298 24.0761 12.3298C22.2825 12.3298 20.7861 12.7034 18.914 13.6216C17.2286 14.447 15.4096 16.3742 15.019 17.7462C14.9999 17.818 14.9808 17.8855 14.9659 17.951C14.845 18.4512 14.4141 18.8037 13.9153 18.8143H13.8558C13.512 18.8143 13.1936 18.6623 12.9792 18.3942C12.7712 18.1325 12.6948 17.7948 12.7712 17.4697C12.7945 17.3684 12.8221 17.2628 12.854 17.151C13.5141 14.8227 15.902 12.6042 17.9206 11.6164C20.0984 10.5504 21.9408 10.0966 24.0867 10.0966C24.212 10.0966 24.3372 10.0966 24.4624 10.1008C26.1223 10.143 27.7843 10.7488 29.0218 11.762C30.4057 12.8976 31.1699 14.4385 31.1699 16.0998C31.1699 19.5278 28.3659 22.3288 24.9188 22.3415C24.8785 22.3415 24.8063 22.3457 24.7087 22.3457L24.7129 22.3478Z" fill="#F7A273" />
      <path d="M24.7488 26.5568C19.7925 26.5252 17.6912 23.2661 17.4683 22.8946C17.2773 22.5948 16.3412 20.9948 16.7721 19.1183C17.0777 17.7863 17.9713 16.676 19.4274 15.819C20.3126 15.2977 21.3187 15.0338 22.4182 15.0338C23.9741 15.0338 25.1733 15.5615 25.307 15.6206C25.5787 15.7452 25.7846 15.9668 25.8865 16.2433C25.9884 16.5198 25.9778 16.8238 25.8525 17.0919C25.67 17.4845 25.2731 17.7378 24.8379 17.7378C24.6766 17.7378 24.5196 17.704 24.371 17.6365C24.3625 17.6322 23.4965 17.2544 22.4224 17.2544C21.7241 17.2544 21.0979 17.4148 20.5652 17.7293C19.6631 18.2613 19.1197 18.8903 18.952 19.5974C18.6994 20.6634 19.3277 21.6639 19.3553 21.7062L19.3892 21.761C19.4529 21.8666 20.9472 24.313 24.7297 24.3384H24.787C28.6501 24.3384 30.7409 21.8645 30.8279 21.7589C31.0402 21.5014 31.3564 21.3536 31.6918 21.3536C31.9465 21.3536 32.197 21.4423 32.3944 21.6027C32.8699 21.9869 32.942 22.6856 32.5578 23.1584C32.4453 23.2977 29.7369 26.559 24.7828 26.559H24.7467L24.7488 26.5568Z" fill="#B76BAB" />
      <path d="M33.3328 21.0898C33.1778 21.0898 33.0271 21.0581 32.8849 20.9948C32.6111 20.8745 32.4031 20.657 32.2948 20.3805C32.1866 20.104 32.193 19.8022 32.3139 19.532C32.7745 18.4913 33.008 17.3789 33.008 16.2306C33.008 14.0438 32.1356 11.9836 30.5586 10.4279C28.9963 8.88915 26.9459 8.0406 24.7808 8.0406H24.7023C23.6474 8.04904 22.62 8.25379 21.6479 8.65063C21.512 8.70551 21.3698 8.73295 21.2255 8.73295C20.7713 8.73295 20.3658 8.46276 20.1918 8.04271C19.9583 7.47701 20.2321 6.82687 20.801 6.59468C22.0342 6.09441 23.3396 5.83267 24.6811 5.82211H24.7617C27.5275 5.82211 30.1446 6.89864 32.125 8.85327C34.1288 10.829 35.2325 13.4485 35.2346 16.2306C35.2346 17.6892 34.9396 19.1014 34.3538 20.4291C34.1755 20.8322 33.7743 21.0919 33.3307 21.0919L33.3328 21.0898Z" fill="#F7B3B4" />
      <path d="M22.3671 33.2609C22.0593 33.2609 21.7749 33.1405 21.5627 32.9189L15.6576 26.7996C15.2309 26.3584 15.2458 25.6534 15.6894 25.2313C15.8974 25.0307 16.1734 24.921 16.4621 24.921C16.7698 24.921 17.0543 25.0413 17.2665 25.2629L23.1716 31.3822C23.5982 31.8234 23.5834 32.5284 23.1397 32.9506C22.9317 33.1511 22.6558 33.2609 22.3671 33.2609Z" fill="#F7A273" />
      <path d="M24.9484 30.4323C24.6448 30.4323 24.3604 30.312 24.1481 30.0967L23.7448 29.6851C23.5368 29.4719 23.4243 29.1933 23.4286 28.8956C23.4328 28.6001 23.5517 28.3215 23.7661 28.1146C23.9762 27.912 24.2522 27.8001 24.5451 27.8001C24.838 27.8001 25.133 27.9204 25.3453 28.1357L25.7486 28.5473C25.9566 28.7605 26.0691 29.0392 26.0648 29.3368C26.0606 29.6323 25.9417 29.9109 25.7274 30.1178C25.5172 30.3204 25.2413 30.4323 24.9484 30.4323Z" fill="#B76BAB" />
      <path d="M19.6524 36.0028C19.3446 36.0028 19.0581 35.8804 18.8458 35.6608L8.82505 25.2376C8.61916 25.0222 8.50879 24.7415 8.51515 24.446C8.52152 24.1505 8.64463 23.874 8.85901 23.6692C9.06703 23.4708 9.34084 23.361 9.62952 23.361C9.93729 23.361 10.2238 23.4835 10.4361 23.7051L20.4569 34.1284C20.6628 34.3437 20.7731 34.6244 20.7668 34.9199C20.7604 35.2155 20.6373 35.492 20.4229 35.6967C20.2149 35.8951 19.9411 36.0049 19.6524 36.0049V36.0028Z" fill="#B76BAB" />
      <path d="M14.4543 25.1067C14.1466 25.1067 13.8621 24.9864 13.6499 24.7647L10.4426 21.4423C8.37735 19.3821 8.354 16.8576 8.354 16.7499C8.354 13.666 10.4384 10.9261 13.6626 9.76728C13.7857 9.72295 13.9131 9.70184 14.0426 9.70184C14.5117 9.70184 14.9319 9.99736 15.0911 10.4343C15.193 10.7129 15.1782 11.0148 15.0508 11.2829C14.9234 11.5509 14.7006 11.7536 14.4204 11.8549C12.0537 12.7056 10.5849 14.58 10.5849 16.7499C10.5849 16.8175 10.6273 18.4871 12.0303 19.8824L15.2609 23.2281C15.4668 23.4413 15.5772 23.7241 15.5708 24.0196C15.5645 24.3151 15.4435 24.5917 15.227 24.7985C15.019 24.9969 14.743 25.1067 14.4543 25.1067Z" fill="#F7B3B4" />
      <path d="M7.59841 23.5172C7.29063 23.5172 7.0062 23.3969 6.79394 23.1753L6.30362 22.6623C6.18051 22.5125 6.07014 22.371 5.96189 22.2254C5.78147 21.9826 5.71354 21.685 5.76448 21.3874C5.81755 21.0877 5.99372 20.8196 6.24631 20.6528C6.43098 20.5304 6.64536 20.4671 6.86611 20.4671C7.21846 20.4671 7.55171 20.6338 7.75973 20.9146C7.83614 21.018 7.9168 21.1214 7.99958 21.2249L8.37952 21.6175C8.79131 22.0439 8.81466 22.713 8.43259 23.1415C8.22033 23.38 7.91467 23.5172 7.59629 23.5172H7.59841Z" fill="#F7A273" />
      <path d="M5.46733 19.9077C4.97064 19.9077 4.52702 19.5742 4.38693 19.0972C3.98788 17.7293 3.89873 16.2581 4.11736 14.7214C4.4315 12.5156 5.43549 10.5061 7.01682 8.91029C8.60028 7.3145 10.6083 6.29075 12.82 5.9509C13.3655 5.86647 13.9216 5.82214 14.4693 5.82214C15.8107 5.82214 17.114 6.067 18.3409 6.55249C18.6189 6.66225 18.8354 6.87123 18.9543 7.14352C19.0732 7.41582 19.0774 7.71767 18.967 7.99208C18.7972 8.41847 18.3897 8.69287 17.9291 8.69287C17.789 8.69287 17.6489 8.66754 17.5173 8.61477C16.5537 8.23482 15.5284 8.04063 14.4714 8.04063C14.0363 8.04063 13.5948 8.0744 13.1617 8.14195C9.63823 8.68443 6.82791 11.5172 6.32698 15.0317C6.15505 16.2433 6.2251 17.4022 6.54136 18.4766C6.69844 19.0106 6.43099 19.5848 5.92369 19.8106C5.77935 19.874 5.62652 19.9077 5.46945 19.9077H5.46733Z" fill="#F4CB72" />
    </svg>
  </Box>
);

const Topic = ({ title, link, imageUrl }: { title: string; link: string; imageUrl: string }) => (
  <TopicStyle>
    <Image src={imageUrl} />
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#CC6699',
        lineHeight: '14px',
        fontSize: '14px',
        fontFamily: 'Quincy CF',
        letterSpacing: 'normal',
        textDecoration: 'none',
      }}
    >
      {title}
    </a>
  </TopicStyle>
);

const Chat = forwardRef(
  ({ queries }: { queries: { query: string; response: string }[] }, ref: Ref<HTMLDivElement>) => (
    <ChatStyle ref={ref}>
      {queries.map((query, index) => (
        <Box id={`chat-${query}`} key={index} display="grid" my="16px" gap="18px">
          <MessageStyle
            sx={{ border: '1px solid #CC6699', marginLeft: '12px', borderBottomLeftRadius: 0 }}
          >
            <span>You:</span>
            <p>{query.query}</p>
            <svg
              width="15"
              height="15"
              viewBox="-20 -20 20 -20"
              fill="white"
              stroke="#CC6699"
              stroke-width="1"
              style={{ left: 0, transform: 'rotate(270deg) translate(-1px, -15px)' }}
            >
              <polygon points="20 20, 0 20, 0 0" />
            </svg>
          </MessageStyle>
          <MessageStyle
            sx={{ background: '#FFD8D6', marginRight: '12px', borderBottomRightRadius: 0 }}
          >
            <span>Empy:</span>
            <p dangerouslySetInnerHTML={{ __html: query.response }} />
            <svg
              width="15"
              height="15"
              viewBox="-20 -20 20 -20"
              fill="#FFD8D6"
              stroke="#FFD8D6"
              stroke-width="1"
              style={{ right: 0, transform: 'rotate(0deg) translate(15px, 0px)' }}
            >
              <polygon points="20 20, 0 20, 0 0" />
            </svg>
          </MessageStyle>
        </Box>
      ))}
    </ChatStyle>
  )
);

const Stars = ({ setScore, score = 0 }: { setScore: (score: number) => void; score?: number }) => (
  <Box display="flex">
    {Array.from(Array(score).keys()).map((index) => (
      <IconButton key={index} onClick={() => setScore(index + 1)} size="small">
        <StarIcon sx={{ color: '#F79669' }} />
      </IconButton>
    ))}
    {Array.from(Array(5 - score).keys()).map((index) => (
      <IconButton key={index} onClick={() => setScore(index + score + 1)} size="small">
        <StarOutlineIcon sx={{ color: '#F79669' }} />
      </IconButton>
    ))}
  </Box>
);

export default function Empy() {
  const chatRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState(false);
  const [activeForm, setActiveForm] = useState('query');
  const { showEmpy, chat } = useSelector((state) => state.global);
  const [queries, setQueries] = useState<{ query: string; response: string }[]>(chat);

  useEffect(() => {
    setIsOpen(showEmpy);
    setActiveForm('query');
    reset();
    resetContact();
    resetFeedback();
  }, [showEmpy]);

  useEffect(() => {
    chatRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth' });
    dispatch(updateChatConversation(queries));
  }, [queries]);

  const querySchemma = Yup.object().shape({
    query: Yup.string()
      .trim()
      .required('Please, insert something before submit')
      .min(3, 'Please, insert at least 3 characters'),
  });

  const contactSchemma = Yup.object().shape({
    name: Yup.string().trim().required('Name is required'),
    email: Yup.string().trim().email('Please, insert a valid email').required('Email is required'),
    phone: Yup.lazy((value) =>
      value === ''
        ? Yup.string()
        : Yup.string()
          .trim()
          .matches(/^[0-9]+$/, 'Phone must contains only numbers')
    ),
    enquiry: Yup.string().trim().required('Enquiry is required'),
  });

  const feedbackSchemma = Yup.object().shape({
    experience: Yup.number(),
    value: Yup.number(),
    feedback: Yup.string().trim(),
    suggestions: Yup.string().trim(),
    email: Yup.string().trim().email('Please, insert a valid email'),
  });

  const methods = useForm<{ query: string }>({
    resolver: yupResolver(querySchemma),
    mode: 'onChange',
  });

  const methodsContact = useForm<{ name: string; email: string; phone: number; enquiry: string }>({
    resolver: yupResolver(contactSchemma),
    mode: 'onChange',
  });

  const methodsFeedback = useForm<{
    experience: number;
    value: number;
    feedback: string;
    suggestions: string;
    email: string;
  }>({
    resolver: yupResolver(feedbackSchemma),
    defaultValues: {
      experience: 0,
      value: 0,
      feedback: '',
      suggestions: '',
      email: '',
    },
    mode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = methods;

  const {
    reset: resetContact,
    handleSubmit: handleSubmitContact,
    formState: {
      errors: errorsContact,
      isSubmitting: isSubmittingContact,
      isDirty: isDirtyContact,
      isValid: isValidContact,
    },
  } = methodsContact;

  const {
    reset: resetFeedback,
    handleSubmit: handleSubmitFeedback,
    formState: {
      errors: errorsFeedback,
      isSubmitting: isSubmittingFeedback,
      isDirty: isDirtyFeedback,
      isValid: isValidFeedback,
    },
  } = methodsFeedback;

  const onSubmit = async (data: { query: string }) => {
    try {
      await services
        .sendQuestion({
          question: data.query,
          conversation: queries.map(({ query, response }) => ({
            question: query,
            answer: response,
          })),
        })
        .then((response: any) => {
          setQueries([
            ...queries,
            {
              query: data.query,
              response: response.data.answer?.replace(/\\n/g, '<br />').replace(/\\"/g, '"'),
            },
          ]);
          reset();
        });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Oops! something went wrong, please try again');
    }
  };

  const onSubmitContact = async (data: {
    name: string;
    email: string;
    phone: number;
    enquiry: string;
  }) => {
    try {
      services.sendContactUs(data).then((response: any) => {
        enqueueSnackbar('Enquiry submitted successfully. Thank you!');
        dispatch(setShowEmpy(false));
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Oops! something went wrong, please try again');
    }
  };

  const onSubmitFeedback = async (data: {
    experience: number;
    value: number;
    feedback: string;
    suggestions: string;
    email: string;
  }) => {
    try {
      services.sendFeedback(data).then((response: any) => {
        enqueueSnackbar('Feedback submitted successfully. Thank you!');
        dispatch(setShowEmpy(false));
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Oops! something went wrong, please try again');
    }
  };

  return isOpen ? (
    <WidgetStyle>
      <IconButton
        onClick={() => {
          setQueries([]);
          dispatch(setShowEmpy(false));
        }}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <SvgIconStyle
          src={`/assets/hub/ic_close.svg`}
          sx={{ background: '#FFFFFF', width: '32px', height: '32px' }}
        />
      </IconButton>
      <Logo />
      {activeForm === 'query' && (
        <FormContainerStyle>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <ContentStyle>
              <Box display="flex" gap="32px" minHeight={56}>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="query"
                  inputProps={{}}
                  disabled={isSubmitting}
                  error={isDirty && Boolean(errors.query)}
                  helperText={isDirty ? errors?.query?.message : ''}
                  placeholder="How can we help?"
                />
                {isSubmitting && (
                  <Box sx={{ display: 'flex' }}>
                    <CircularProgress color="inherit" size={24} />
                  </Box>
                )}
                <ButtonStyle
                  disabled={!isDirty || !isValid}
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  sx={{ mt: '8px' }}
                >
                  Submit
                </ButtonStyle>
              </Box>
              {queries.length > 0 && <Chat ref={chatRef} queries={queries} />}
              {queries.length === 0 && (
                <Box>
                  <LabelStyle sx={{ mb: '8px' }}>Explore popular topics</LabelStyle>
                  <Box display="flex" justifyContent="space-between" gap="32px">
                    <Topic
                      title="Creating a Culture of Innovation: The Identity Imperative"
                      link="https://medium.com/emplify-stories/creating-a-culture-of-innovation-the-identity-imperative-5b26f8898398"
                      imageUrl="/assets/empy/imperative.png"
                    />
                    <Topic
                      title="Unleashing Innovation in 2024 Starts With Your Culture"
                      link="https://medium.com/emplify-stories/unleashing-innovation-in-2024-starts-with-your-culture-7db4d6c83e5b"
                      imageUrl="/assets/empy/unleash.png"
                    />
                    <Topic
                      title="Houston, we have a problem"
                      link="https://medium.com/emplify-stories/houston-we-have-a-problem-f331f98da067"
                      imageUrl="/assets/empy/houston.png"
                    />
                  </Box>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap="16px">
                  <LabelStyle>We’d love to hear from you</LabelStyle>
                  <ButtonStyle variant="outlined" onClick={() => setActiveForm('feedback')}>
                    Feedback
                  </ButtonStyle>
                </Box>
                <Box display="flex" alignItems="center" gap="16px">
                  <LabelStyle>Need to get in touch?</LabelStyle>
                  <ButtonStyle onClick={() => setActiveForm('contact')}>Contact</ButtonStyle>
                </Box>
              </Box>
            </ContentStyle>
          </FormProvider>
          <ArrowStyle />
        </FormContainerStyle>
      )}
      {activeForm === 'contact' && (
        <FormContainerStyle>
          <TitleStyle>
            Get in <span>Touch</span>
          </TitleStyle>
          <Typography sx={{ transform: 'translateY(-14px)', mb: '8px' }}>
            Send us a message and we’ll get back to you shortly
          </Typography>
          <FormProvider methods={methodsContact} onSubmit={handleSubmitContact(onSubmitContact)}>
            <ContentStyle>
              <Box display="flex" flexDirection="column">
                <LabelStyle>Your name</LabelStyle>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="name"
                  inputProps={{}}
                  disabled={isSubmittingContact}
                  error={isDirtyContact && Boolean(errorsContact.name)}
                  helperText={isDirtyContact ? errorsContact?.name?.message : ''}
                  placeholder="Name"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <LabelStyle>Your email</LabelStyle>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="email"
                  inputProps={{}}
                  disabled={isSubmittingContact}
                  error={isDirtyContact && Boolean(errorsContact.email)}
                  helperText={isDirtyContact ? errorsContact?.email?.message : ''}
                  placeholder="Your email address"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <Box display="flex" gap="4px">
                  <LabelStyle>Your phone number</LabelStyle>
                  <Typography fontSize="12px">
                    (Optional - Please state your country prefix number)
                  </Typography>
                </Box>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="phone"
                  inputProps={{}}
                  disabled={isSubmittingContact}
                  error={isDirtyContact && Boolean(errorsContact.phone)}
                  helperText={isDirtyContact ? errorsContact?.phone?.message : ''}
                  placeholder="Your phone number"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <LabelStyle>How can we help?</LabelStyle>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="enquiry"
                  inputProps={{}}
                  disabled={isSubmittingContact}
                  error={isDirtyContact && Boolean(errorsContact.enquiry)}
                  helperText={isDirtyContact ? errorsContact?.enquiry?.message : ''}
                  placeholder="Pop your enquiry in here"
                  multiline={true}
                />
              </Box>
              <ButtonStyle
                disabled={!isDirtyContact || !isValidContact}
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmittingContact}
                sx={{ alignSelf: 'end' }}
              >
                Send
              </ButtonStyle>
            </ContentStyle>
          </FormProvider>
          <ArrowStyle />
        </FormContainerStyle>
      )}
      {activeForm === 'feedback' && (
        <FormContainerStyle>
          <TitleStyle>
            Help us improve <span>Emplify</span>
          </TitleStyle>
          <FormProvider methods={methodsFeedback} onSubmit={handleSubmitFeedback(onSubmitFeedback)}>
            <ContentStyle>
              <Box display="flex" flexDirection="column">
                <LabelStyle>How would you rate your user experience with Emplify?</LabelStyle>
                <Typography fontSize="13px" my="4px">
                  1 - Very hard 5 - Very easy
                </Typography>
                <Stars
                  score={methodsFeedback.watch('experience')}
                  setScore={(score: number) =>
                    methodsFeedback.watch('experience') === score
                      ? methodsFeedback.setValue('experience', 0, { shouldDirty: true })
                      : methodsFeedback.setValue('experience', score, { shouldDirty: true })
                  }
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <LabelStyle>How would you rate Emplify’s value to your organisation?</LabelStyle>
                <Typography fontSize="13px" my="4px">
                  1 - Poor 5 - Amazing
                </Typography>
                <Stars
                  score={methodsFeedback.watch('value')}
                  setScore={(score: number) =>
                    methodsFeedback.watch('value') === score
                      ? methodsFeedback.setValue('value', 0, { shouldDirty: true })
                      : methodsFeedback.setValue('value', score, { shouldDirty: true })
                  }
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <LabelStyle>Is there anything confusing or missing?</LabelStyle>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="feedback"
                  inputProps={{}}
                  disabled={isSubmittingFeedback}
                  placeholder="Feed back here"
                  multiline={true}
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <LabelStyle>Improvement suggestions</LabelStyle>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="suggestions"
                  inputProps={{}}
                  disabled={isSubmittingFeedback}
                  placeholder="Share your thoughts here"
                  multiline={true}
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <LabelStyle>Can we contact you for additional feedback?</LabelStyle>
                <TextFieldStyle
                  variant="outlined"
                  margin="normal"
                  name="email"
                  inputProps={{}}
                  disabled={isSubmittingFeedback}
                  error={isDirtyFeedback && Boolean(errorsFeedback.email)}
                  helperText={isDirtyFeedback ? errorsFeedback?.email?.message : ''}
                  placeholder="Please provide your email address here"
                />
              </Box>
              <ButtonStyle
                disabled={!isDirtyFeedback || !isValidFeedback}
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmittingFeedback}
                sx={{ alignSelf: 'start' }}
              >
                Submit
              </ButtonStyle>
            </ContentStyle>
          </FormProvider>
          <ArrowStyle />
        </FormContainerStyle>
      )}
    </WidgetStyle>
  ) : (
    <BotStyle
      disableRipple
      disableFocusRipple
      disableTouchRipple
      onClick={() => dispatch(setShowEmpy(true))}
    >
      <Image src={'/assets/empy.png'} />
    </BotStyle>
  );
}
